<template>
  <ListPageShell>
    <template #header>
      <div class="list-page-panel is-header-minimal">
        <div class="list-page-heading">
          <div class="list-page-heading-main">
            <h1 class="list-page-title">合同范本</h1>
            <p class="list-page-description">标准化管理合同模板，支持版本迭代、附件管理和模板下载。</p>
          </div>
          <div class="flex flex-wrap items-center gap-3">
            <button
              v-permission="'kb:contract:edit'"
              @click="openFormModal()"
              class="btn-primary inline-flex items-center gap-2"
            >
              <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
              </svg>
              新增范本
            </button>
          </div>
        </div>
      </div>
    </template>
    <div class="min-h-full overflow-x-hidden bg-gradient-to-br from-slate-50 via-white to-slate-100">
    <div class="relative overflow-hidden">
      <div class="pointer-events-none absolute -right-40 -top-24 h-72 w-72 rounded-full bg-primary-100/60 blur-3xl"></div>
      <div class="pointer-events-none absolute left-10 top-36 h-40 w-40 rounded-full bg-slate-200/60 blur-3xl"></div>

      <div class="mx-auto w-full px-4 py-6">

        <!-- 主体区域 -->
        <div class="grid grid-cols-1 gap-6 lg:grid-cols-12">
          <!-- 左侧分类树 -->
          <div class="lg:col-span-3">
            <KbCategoryTree
              :tree="categoryTree"
              :loading="categoryLoading"
              :selected-id="selectedCategoryId"
              
              title="合同分类"
              description="按合同类型分类管理范本。"
              @add="handleAddCategory"
              @add-child="handleAddChildCategory"
              @edit="handleEditCategory"
              @delete="confirmDeleteCategory"
              @select="handleCategoryTreeSelect"
            />
          </div>

          <!-- 右侧列表 -->
          <div class="lg:col-span-9">
            <div class="list-page-table-panel">
              <!-- 搜索栏 -->
              <div class="border-b border-slate-200 p-5">
                <h2 class="text-base font-semibold text-slate-900">范本列表</h2>
                <PageFilterBar
                  shell-class="filter-bar-shell is-embedded"
                  body-class="filter-bar-body is-embedded"
                  :show-refresh="true"
                  :refresh-loading="listLoading"
                  @refresh="loadList(pageInfo.current)"
                >
                  <div class="filter-bar-field is-wide relative">
                    <input
                      v-model="searchKeyword"
                      type="text"
                      placeholder="搜索范本名称或编号"
                      class="form-search-input form-control-sm w-full"
                      @keyup.enter="loadList(1)"
                    >
                    <svg class="absolute left-3 top-2.5 h-4 w-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <el-select v-model="searchStatus" placeholder="请选择状态" class="form-select form-control-sm filter-bar-field">
                    <el-option label="全部状态" value="" />
                    <el-option label="草稿" value="0" />
                    <el-option label="已发布" value="1" />
                    <el-option label="已废止" value="2" />
                  </el-select>

                  <template #searchActions>
                    <button @click="loadList(1)" class="btn-primary">搜索</button>
                    <button @click="resetFilters" class="btn-secondary">重置</button>
                  </template>
                </PageFilterBar>
              </div>

              <!-- 表格 -->
              <div class="data-table-shell">
                <div class="data-table-scroll">
                <table class="data-table text-sm">
                  <thead class="data-table-head">
                    <tr>
                      <th class="data-table-th text-left">模板名称</th>
                      <th class="data-table-th text-left">分类</th>
                      <th class="data-table-th is-center">状态</th>
                      <th class="data-table-th is-center">版本</th>
                      <th class="data-table-th is-center">下载次数</th>
                      <th class="data-table-th is-center">创建时间</th>
                      <th class="data-table-th is-center">操作</th>
                    </tr>
                  </thead>
                  <tbody class="data-table-body">
                    <template v-if="listLoading">
                      <tr v-for="n in 5" :key="n" class="animate-pulse">
                        <td class="data-table-td"><div class="h-4 w-40 rounded bg-slate-200"></div></td>
                        <td class="data-table-td"><div class="h-4 w-16 rounded bg-slate-200"></div></td>
                        <td class="data-table-td is-center"><div class="mx-auto h-5 w-14 rounded-full bg-slate-200"></div></td>
                        <td class="data-table-td is-center"><div class="mx-auto h-4 w-8 rounded bg-slate-200"></div></td>
                        <td class="data-table-td is-center"><div class="mx-auto h-4 w-10 rounded bg-slate-200"></div></td>
                        <td class="data-table-td is-center"><div class="mx-auto h-4 w-24 rounded bg-slate-200"></div></td>
                        <td class="data-table-td"><div class="mx-auto flex justify-center gap-2"><div class="h-7 w-7 rounded bg-slate-200"></div><div class="h-7 w-7 rounded bg-slate-200"></div></div></td>
                      </tr>
                    </template>
                    <tr v-else-if="!dataList.length">
                      <td colspan="7" class="data-table-empty text-sm text-slate-500">暂无合同范本数据</td>
                    </tr>
                    <tr v-for="item in dataList" :key="item.id" class="data-table-row">
                      <td class="data-table-td">
                        <div class="max-w-xs">
                          <div class="data-table-cell-title truncate">{{ item.name }}</div>
                          <div v-if="item.description" class="data-table-cell-meta truncate">{{ item.description }}</div>
                        </div>
                      </td>
                      <td class="data-table-td text-slate-600">{{ getCategoryName(item.categoryId) }}</td>
                      <td class="data-table-td is-center">
                        <span :class="contractStatusClass(item.status)">{{ contractStatusText(item.status) }}</span>
                      </td>
                      <td class="data-table-td is-center">
                        <button @click="openVersionDrawer(item)" class="text-xs text-primary-600 hover:text-primary-700 hover:underline">
                          v{{ item.currentVersion || 1 }}
                        </button>
                      </td>
                      <td class="data-table-td is-center text-slate-600">{{ item.downloadCount || 0 }}</td>
                      <td class="data-table-td is-center text-xs text-slate-500">{{ item.createTime?.slice(0, 10) || '-' }}</td>
                      <td class="data-table-td">
                        <div class="flex items-center justify-center gap-1">
                          <button @click="openDetailModal(item)" class="table-action-button is-primary" title="查看详情" aria-label="查看详情">
                            <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                            </svg>
                          </button>
                          <button v-permission="'kb:contract:edit'" @click="openFormModal(item)" class="table-action-button is-primary" title="编辑" aria-label="编辑">
                            <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                            </svg>
                          </button>
                          <div class="relative more-menu-container">
                            <button
                              v-permission="'kb:contract:edit'"
                              :ref="el => setMoreBtnRef(item.id, el)"
                              @click.stop="toggleMoreMenu(item.id, $event)"
                              class="table-action-button is-neutral"
                              title="更多"
                            >
                              <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
                </div>
              </div>

              <!-- 分页 -->
              <AppPagination
                v-if="pageInfo.total > 0"
                class="pagination-bridge"
                :pagination="pageInfo"
                :size="pageSize"
                :page-sizes="[10, 20, 50]"
                @page-change="handlePageChange"
                @size-change="handlePageSizeChange"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 分类弹窗 -->
    <Transition name="modal">
      <div v-if="showCategoryModal" class="fixed inset-0 z-50">
        <div class="absolute inset-0 bg-black/60 backdrop-blur-sm" @click="closeCategoryModal"></div>
        <div class="relative mx-auto flex min-h-screen w-full max-w-md items-center px-4 py-8">
          <div class="w-full overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-black/5">
            <!-- 弹窗头部 -->
            <div class="relative bg-gradient-to-r from-primary-600 to-primary-500 px-6 py-5">
              <div class="absolute inset-0 bg-grid-white/10"></div>
              <div class="relative flex items-center justify-between">
                <div>
                  <h3 class="text-lg font-semibold text-white">{{ isCategoryEdit ? '编辑分类' : '新增分类' }}</h3>
                  <p class="mt-0.5 text-sm text-primary-100">{{ isCategoryEdit ? '修改分类信息' : '创建新的合同分类' }}</p>
                </div>
                <button @click="closeCategoryModal" class="cursor-pointer rounded-full p-1.5 text-white/80 transition-colors hover:bg-white/20 hover:text-white">
                  <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
            </div>

            <!-- 基础信息 -->
            <div class="space-y-5 px-6 py-6">
              <div class="space-y-1.5">
                <label class="flex items-center gap-1 text-sm font-medium text-slate-700">
                  <svg class="h-4 w-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" /></svg>
                  分类名称 <span class="text-red-500">*</span>
                </label>
                <input v-model="categoryForm.name" class="form-input" placeholder="请输入分类名称">
              </div>
              <div class="space-y-1.5">
                <label class="flex items-center gap-1 text-sm font-medium text-slate-700">
                  <svg class="h-4 w-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" /></svg>
                  上级分类
                </label>
                <el-select v-model="categoryForm.parentId" class="form-select">
                  <el-option label="顶级分类" :value="0" />
                  <el-option v-for="cat in flatCategories" :key="cat.id" :label="cat.label" :value="cat.id" />
                </el-select>
              </div>
            </div>

            <!-- 分隔线 -->
            <div class="mx-6 border-t border-dashed border-slate-200"></div>

            <!-- 可选信息 -->
            <div class="space-y-5 px-6 py-6">
              <p class="text-xs font-medium text-slate-400 uppercase tracking-wider">可选信息</p>
              <div class="grid grid-cols-2 gap-4">
                <div class="space-y-1.5">
                  <label class="flex items-center gap-1 text-sm font-medium text-slate-700">
                    <svg class="h-4 w-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" /></svg>
                    排序号
                  </label>
                  <input v-model.number="categoryForm.sort" type="number" class="form-input" placeholder="0">
                </div>
                <div class="space-y-1.5">
                  <label class="flex items-center gap-1 text-sm font-medium text-slate-700">
                    <svg class="h-4 w-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    状态
                  </label>
                  <el-select v-model="categoryForm.status" class="form-select">
                    <el-option label="启用" :value="0" />
                    <el-option label="停用" :value="1" />
                  </el-select>
                </div>
              </div>
            </div>

            <!-- 底部按钮 -->
            <div class="flex justify-end gap-3 border-t border-slate-100 bg-slate-50/50 px-6 py-4">
              <button @click="closeCategoryModal" class="btn-secondary">取消</button>
              <button @click="submitCategory" class="btn-primary hover:shadow-md hover:shadow-primary-500/30">
                {{ isCategoryEdit ? '保存修改' : '创建分类' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>

    <!-- 范本新增/编辑弹窗 -->
    <div v-if="showFormModal" class="fixed inset-0 z-50">
      <div class="absolute inset-0 bg-black/60 backdrop-blur-sm" @click="closeFormModal"></div>
      <div class="relative min-h-screen flex items-center justify-center p-4">
        <div class="relative bg-white rounded-2xl shadow-2xl ring-1 ring-black/5 max-w-3xl w-full max-h-[90vh] overflow-hidden">
          <!-- 弹窗头部 -->
          <div class="relative bg-gradient-to-r from-primary-600 to-primary-500 px-6 py-5">
            <div class="absolute inset-0 bg-grid-white/10"></div>
            <div class="relative flex items-center justify-between">
              <div>
                <h3 class="text-lg font-semibold text-white">{{ isEdit ? '编辑合同范本' : '新增合同范本' }}</h3>
                <p class="mt-0.5 text-sm text-primary-100">{{ isEdit ? '修改范本信息和附件' : '创建新的合同模板' }}</p>
              </div>
              <button @click="closeFormModal" class="rounded-full p-1.5 text-white/80 transition-colors hover:bg-white/20 hover:text-white">
                <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
          </div>
          <!-- 表单内容 -->
          <div class="overflow-y-auto max-h-[calc(90vh-180px)] px-6 py-6 space-y-5">
            <!-- 基本信息区块 -->
            <div class="space-y-4">
              <div class="flex items-center gap-2 text-sm font-medium text-slate-900">
                <div class="flex h-6 w-6 items-center justify-center rounded-full bg-primary-100 text-primary-600">
                  <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                </div>
                基本信息
              </div>
              <div class="grid grid-cols-1 gap-4 rounded-xl border border-slate-200 bg-slate-50/30 p-4 lg:grid-cols-2">
                <div class="space-y-1.5 lg:col-span-2">
                  <label class="flex items-center gap-1 text-sm font-medium text-slate-700">
                    <svg class="h-4 w-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                    模板名称 <span class="text-red-500">*</span>
                  </label>
                  <input v-model="form.name" class="form-input" placeholder="请输入模板名称">
                </div>
                <div class="space-y-1.5 lg:col-span-2">
                  <label class="flex items-center gap-1 text-sm font-medium text-slate-700">
                    <svg class="h-4 w-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" /></svg>
                    所属分类 <span class="text-red-500">*</span>
                  </label>
                  <el-select v-model="form.categoryId" class="form-select">
                    <el-option label="请选择分类" value="" />
                    <el-option v-for="cat in flatCategories" :key="cat.id" :label="cat.label" :value="cat.id" />
                  </el-select>
                </div>
              </div>
            </div>
            <!-- 描述信息区块 -->
            <div class="space-y-4">
              <div class="flex items-center gap-2 text-sm font-medium text-slate-900">
                <div class="flex h-6 w-6 items-center justify-center rounded-full bg-amber-100 text-amber-600">
                  <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h7" /></svg>
                </div>
                描述信息
              </div>
              <div class="space-y-4 rounded-xl border border-slate-200 bg-slate-50/30 p-4">
                <div class="space-y-1.5">
                  <label class="text-sm font-medium text-slate-700">模板描述</label>
                  <textarea v-model="form.description" rows="3" class="form-textarea" placeholder="请输入模板描述，帮助用户了解模板用途"></textarea>
                </div>
                <div class="space-y-1.5">
                  <label class="text-sm font-medium text-slate-700">备注</label>
                  <textarea v-model="form.remark" rows="2" class="form-textarea" placeholder="可选备注信息"></textarea>
                </div>
              </div>
            </div>
            <!-- 附件管理区块 -->
            <div class="space-y-4">
              <div class="flex items-center gap-2 text-sm font-medium text-slate-900">
                <div class="flex h-6 w-6 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
                  <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" /></svg>
                </div>
                模板文件 <span class="text-red-500">*</span>
              </div>
              <div class="rounded-xl border border-slate-200 bg-slate-50/30 p-4 space-y-4">
                <!-- 已有附件（编辑时显示） -->
                <div v-if="isEdit && form.existingAttachments.length" class="space-y-2">
                  <div class="flex items-center gap-2 text-xs font-medium text-slate-500">
                    <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" /></svg>
                    已有附件
                  </div>
                  <div
                    v-for="att in form.existingAttachments"
                    :key="att.id"
                    class="group flex items-center justify-between rounded-lg border border-slate-200 bg-white px-4 py-3 transition-all hover:border-slate-300 hover:shadow-sm"
                  >
                    <div class="flex items-center gap-3 min-w-0">
                      <div class="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
                        <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <div class="min-w-0">
                        <div class="text-sm font-medium text-slate-900 truncate">{{ att.fileName }}</div>
                        <div class="text-xs text-slate-500">{{ formatFileSize(att.fileSize) }}</div>
                      </div>
                    </div>
                    <button @click="removeExistingAttachment(att)" class="cursor-pointer rounded-lg px-3 py-1.5 text-xs font-medium text-red-600 opacity-0 transition-all hover:bg-red-50 group-hover:opacity-100">删除</button>
                  </div>
                </div>
                <!-- 上传新附件 -->
                <div>
                  <label class="group flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-300 bg-white p-6 transition-all hover:border-primary-400 hover:bg-primary-50/30">
                    <div class="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-400 transition-colors group-hover:bg-primary-100 group-hover:text-primary-600">
                      <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                    </div>
                    <p class="mt-3 text-sm font-medium text-slate-700 group-hover:text-primary-700">点击选择文件上传</p>
                    <p class="mt-1 text-xs text-slate-500">支持 PDF、DOC、DOCX 格式，可上传多个文件</p>
                    <input type="file" class="hidden" multiple accept=".pdf,.doc,.docx" @change="handleAttachmentSelect">
                  </label>
                </div>
                <!-- 待上传的新文件 -->
                <div v-if="form.files.length" class="space-y-2">
                  <div class="flex items-center gap-2 text-xs font-medium text-primary-600">
                    <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
                    待上传
                  </div>
                  <div v-for="(file, idx) in form.files" :key="idx" class="group flex items-center justify-between rounded-lg border border-primary-200 bg-primary-50/50 px-4 py-3 transition-all">
                    <div class="flex items-center gap-3 min-w-0">
                      <div class="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                        <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <div class="min-w-0">
                        <div class="text-sm font-medium text-slate-900 truncate">{{ file.name }}</div>
                        <div class="text-xs text-slate-500">{{ formatFileSize(file.size) }}</div>
                      </div>
                    </div>
                    <button @click="removeNewFile(idx)" class="cursor-pointer rounded-lg px-3 py-1.5 text-xs font-medium text-red-600 transition-all hover:bg-red-50">移除</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <!-- 底部按钮 -->
          <div class="flex justify-end gap-3 border-t border-slate-100 bg-slate-50/50 px-6 py-4">
            <button @click="closeFormModal" class="cursor-pointer rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 transition-all hover:bg-slate-50 hover:text-slate-900">取消</button>
            <button @click="submitForm" :disabled="formSubmitting" class="cursor-pointer rounded-lg bg-primary-600 px-5 py-2 text-sm font-medium text-white shadow-sm shadow-primary-600/30 transition-all hover:bg-primary-700 hover:shadow-md hover:shadow-primary-600/40 disabled:cursor-not-allowed disabled:opacity-50 disabled:shadow-none">
              <span v-if="formSubmitting" class="flex items-center gap-2">
                <svg class="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                保存中...
              </span>
              <span v-else>保存</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 详情弹窗 -->
    <div v-if="showDetailModal" class="fixed inset-0 z-[60]">
      <div class="absolute inset-0 bg-black/60 backdrop-blur-sm" @click="showDetailModal = false"></div>
      <div class="relative min-h-screen flex items-center justify-center p-4">
        <div class="relative bg-white rounded-2xl shadow-2xl ring-1 ring-black/5 max-w-3xl w-full max-h-[90vh] overflow-hidden">
          <!-- 弹窗头部 -->
          <div class="relative bg-gradient-to-r from-primary-600 to-primary-500 px-6 py-5">
            <div class="absolute inset-0 bg-grid-white/10"></div>
            <div class="relative flex items-center justify-between">
              <div class="flex items-center gap-3">
                <div class="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 text-white">
                  <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                </div>
                <div>
                  <h3 class="text-lg font-semibold text-white">合同范本详情</h3>
                  <p class="mt-0.5 text-sm text-primary-100">查看范本信息和附件</p>
                </div>
              </div>
              <button @click="showDetailModal = false" class="rounded-full p-1.5 text-white/80 transition-colors hover:bg-white/20 hover:text-white">
                <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
          </div>
          <!-- 内容区域 -->
          <div class="overflow-y-auto max-h-[calc(90vh-100px)] px-6 py-6">
            <div v-if="detailLoading" class="space-y-4 animate-pulse">
              <div class="h-8 w-3/4 rounded-lg bg-slate-200"></div>
              <div class="h-5 w-1/2 rounded bg-slate-200"></div>
              <div class="h-32 rounded-xl bg-slate-200"></div>
            </div>
            <template v-else-if="detailData">
              <!-- 状态和元信息 -->
              <div class="flex flex-wrap items-center gap-3">
                <span :class="contractStatusClass(detailData.status)">{{ contractStatusText(detailData.status) }}</span>
                <div class="flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">
                  <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" /></svg>
                  v{{ detailData.currentVersion }}
                </div>
                <div class="flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">
                  <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                  {{ detailData.downloadCount || 0 }} 次下载
                </div>
              </div>
              <!-- 标题 -->
              <h2 class="mt-4 text-xl font-bold text-slate-900">{{ detailData.name }}</h2>
              <div class="mt-2 flex items-center gap-2 text-sm text-slate-500">
                <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" /></svg>
                {{ detailData.categoryName || getCategoryName(detailData.categoryId) }}
              </div>
              <!-- 模板描述 -->
              <div v-if="detailData.description" class="mt-5">
                <div class="flex items-center gap-2 text-sm font-medium text-slate-900 mb-3">
                  <div class="flex h-6 w-6 items-center justify-center rounded-full bg-primary-100 text-primary-600">
                    <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h7" /></svg>
                  </div>
                  模板描述
                </div>
                <div class="rounded-xl border border-slate-200 bg-slate-50/50 p-4 text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{{ detailData.description }}</div>
              </div>
              <!-- 备注 -->
              <div v-if="detailData.remark" class="mt-5">
                <div class="flex items-center gap-2 text-sm font-medium text-slate-900 mb-3">
                  <div class="flex h-6 w-6 items-center justify-center rounded-full bg-amber-100 text-amber-600">
                    <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" /></svg>
                  </div>
                  备注
                </div>
                <div class="text-sm text-slate-600">{{ detailData.remark }}</div>
              </div>
              <!-- 模板文件 -->
              <div v-if="detailData.attachments?.length" class="mt-5">
                <div class="flex items-center gap-2 text-sm font-medium text-slate-900 mb-3">
                  <div class="flex h-6 w-6 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
                    <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" /></svg>
                  </div>
                  模板文件
                </div>
                <div class="space-y-2">
                  <div
                    v-for="att in detailData.attachments"
                    :key="att.id"
                    class="group flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3 transition-all hover:border-slate-300 hover:shadow-sm"
                  >
                    <div class="flex items-center gap-3">
                      <div class="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
                        <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <div>
                        <div class="text-sm font-medium text-slate-900">{{ att.fileName }}</div>
                        <div class="text-xs text-slate-500">{{ formatFileSize(att.fileSize) }} · {{ att.fileType?.toUpperCase() }}</div>
                      </div>
                    </div>
                    <div class="flex items-center gap-2">
                      <button
                        class="cursor-pointer rounded-lg px-3 py-1.5 text-xs font-medium transition-all"
                        :class="previewLoading[att.id] ? 'text-slate-300 cursor-not-allowed' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'"
                        :disabled="previewLoading[att.id]"
                        @click="handlePreview(att)"
                      >
                        <span v-if="previewLoading[att.id]" class="flex items-center gap-1">
                          <svg class="h-3.5 w-3.5 animate-spin" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                          加载中
                        </span>
                        <span v-else>预览</span>
                      </button>
                      <button @click="handleDownload(att)" class="cursor-pointer rounded-lg bg-primary-50 px-3 py-1.5 text-xs font-medium text-primary-600 transition-all hover:bg-primary-100">下载</button>
                    </div>
                  </div>
                </div>
              </div>
              <!-- 元信息 -->
              <div class="mt-6 flex flex-wrap items-center gap-4 rounded-xl border border-slate-100 bg-slate-50/50 px-4 py-3 text-xs text-slate-500">
                <div class="flex items-center gap-1.5">
                  <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                  创建人：{{ detailData.createBy || '-' }}
                </div>
                <div class="flex items-center gap-1.5">
                  <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                  创建：{{ detailData.createTime || '-' }}
                </div>
                <div class="flex items-center gap-1.5">
                  <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  更新：{{ detailData.updateTime || '-' }}
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>

    <!-- 新版本弹窗 -->
    <div v-if="showNewVersionModal" class="fixed inset-0 z-50">
      <div class="absolute inset-0 bg-black/60 backdrop-blur-sm" @click="showNewVersionModal = false"></div>
      <div class="relative mx-auto flex min-h-screen w-full max-w-md items-center px-4 py-8">
        <div class="w-full overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-black/5">
          <!-- 弹窗头部 -->
          <div class="relative bg-gradient-to-r from-primary-600 to-primary-500 px-6 py-5">
            <div class="absolute inset-0 bg-grid-white/10"></div>
            <div class="relative flex items-center justify-between">
              <div>
                <h3 class="text-lg font-semibold text-white">创建新版本</h3>
                <p class="mt-0.5 text-sm text-primary-100 truncate max-w-[280px]">{{ newVersionTarget?.name }}</p>
              </div>
              <button @click="showNewVersionModal = false" class="rounded-full p-1.5 text-white/80 transition-colors hover:bg-white/20 hover:text-white">
                <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
          </div>
          <!-- 表单内容 -->
          <div class="px-6 py-6">
            <div class="space-y-1.5">
              <label class="flex items-center gap-1 text-sm font-medium text-slate-700">
                <svg class="h-4 w-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h7" /></svg>
                变更摘要
              </label>
              <textarea v-model="newVersionForm.changeSummary" rows="4" class="form-textarea" placeholder="简要说明本次版本变更的内容，例如：修改了付款条款..."></textarea>
              <p class="text-xs text-slate-500">记录版本变更说明，便于后续追溯和管理</p>
            </div>
          </div>
          <!-- 底部按钮 -->
          <div class="flex justify-end gap-3 border-t border-slate-100 bg-slate-50/50 px-6 py-4">
            <button @click="showNewVersionModal = false" class="cursor-pointer rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 transition-all hover:bg-slate-50 hover:text-slate-900">取消</button>
            <button @click="submitNewVersion" class="cursor-pointer rounded-lg bg-primary-600 px-5 py-2 text-sm font-medium text-white shadow-sm shadow-primary-600/30 transition-all hover:bg-primary-700 hover:shadow-md hover:shadow-primary-600/40">创建版本</button>
          </div>
        </div>
      </div>
    </div>

    <!-- 版本历史抽屉 -->
    <div v-if="showVersionDrawer" class="fixed inset-0 z-50">
      <div class="absolute inset-0 bg-black/60 backdrop-blur-sm" @click="showVersionDrawer = false"></div>
      <div class="absolute right-0 top-0 h-full w-full max-w-lg overflow-hidden bg-white shadow-2xl ring-1 ring-black/5">
        <div class="flex h-full flex-col">
          <!-- 抽屉头部 -->
          <div class="relative bg-gradient-to-r from-primary-600 to-primary-500 px-6 py-5">
            <div class="absolute inset-0 bg-grid-white/10"></div>
            <div class="relative flex items-center justify-between">
              <div class="min-w-0 flex-1 mr-4">
                <div class="flex items-center gap-2">
                  <svg class="h-5 w-5 text-white/80" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  <h3 class="text-lg font-semibold text-white">版本历史</h3>
                </div>
                <p class="mt-1 text-sm text-primary-100 truncate">{{ versionTarget?.name }}</p>
              </div>
              <button @click="showVersionDrawer = false" class="rounded-full p-1.5 text-white/80 transition-colors hover:bg-white/20 hover:text-white">
                <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
          </div>
          <!-- 版本列表 -->
          <div class="flex-1 overflow-y-auto px-6 py-5">
            <div v-if="versionLoading" class="space-y-4 animate-pulse">
              <div v-for="n in 3" :key="n" class="rounded-xl border border-slate-200 p-4">
                <div class="h-5 w-16 rounded-full bg-slate-200"></div>
                <div class="mt-3 h-4 w-40 rounded bg-slate-200"></div>
                <div class="mt-2 h-3 w-24 rounded bg-slate-200"></div>
              </div>
            </div>
            <div v-else class="space-y-3">
              <div
                v-for="version in versionList"
                :key="version.id"
                class="group cursor-pointer rounded-xl border border-slate-200 bg-white p-4 transition-all hover:border-primary-200 hover:shadow-md"
              >
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-2">
                    <span class="rounded-full bg-primary-50 px-3 py-1 text-xs font-semibold text-primary-700">v{{ version.versionNo }}</span>
                    <span v-if="version.versionNo === versionTarget?.currentVersion" class="flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700">
                      <svg class="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" /></svg>
                      当前版本
                    </span>
                  </div>
                  <div class="flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                    <button
                      @click="viewVersionDetail(version)"
                      class="cursor-pointer rounded-lg px-2.5 py-1 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-100"
                    >查看</button>
                    <button
                      v-if="version.versionNo !== versionTarget?.currentVersion"
                      v-permission="'kb:contract:edit'"
                      @click="handleRollback(version)"
                      class="cursor-pointer rounded-lg px-2.5 py-1 text-xs font-medium text-primary-600 transition-colors hover:bg-primary-50"
                    >回滚</button>
                  </div>
                </div>
                <div class="mt-3 text-sm font-medium text-slate-900">{{ version.name }}</div>
                <div v-if="version.changeSummary" class="mt-1.5 text-xs text-slate-500 line-clamp-2">{{ version.changeSummary }}</div>
                <div class="mt-3 flex items-center gap-3 text-xs text-slate-400">
                  <span class="flex items-center gap-1">
                    <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                    {{ version.createTime }}
                  </span>
                  <span class="flex items-center gap-1">
                    <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                    {{ version.createBy || '系统' }}
                  </span>
                </div>
              </div>
              <!-- 空状态 -->
              <div v-if="!versionList.length" class="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-200 py-12">
                <div class="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-400">
                  <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                </div>
                <p class="mt-3 text-sm text-slate-500">暂无版本记录</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 确认删除弹窗 -->
    <div v-if="showConfirmModal" class="fixed inset-0 z-50">
      <div class="absolute inset-0 bg-black/60 backdrop-blur-sm" @click="showConfirmModal = false"></div>
      <div class="relative mx-auto flex min-h-screen max-w-sm items-center px-4 py-8">
        <div class="w-full overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-black/5">
          <!-- 图标区域 -->
          <div class="flex flex-col items-center px-6 pt-8 pb-4">
            <div class="flex h-14 w-14 items-center justify-center rounded-full bg-red-100 text-red-600">
              <svg class="h-7 w-7" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
            </div>
            <h3 class="mt-4 text-lg font-semibold text-slate-900">确认删除</h3>
            <p class="mt-2 text-center text-sm text-slate-600 leading-relaxed">{{ confirmMessage }}</p>
          </div>
          <!-- 底部按钮 -->
          <div class="flex gap-3 border-t border-slate-100 bg-slate-50/50 px-6 py-4">
            <button @click="showConfirmModal = false" class="flex-1 cursor-pointer rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-600 transition-all hover:bg-slate-50 hover:text-slate-900">取消</button>
            <button @click="confirmDeleteAction" class="flex-1 cursor-pointer rounded-lg bg-red-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm shadow-red-600/30 transition-all hover:bg-red-700 hover:shadow-md hover:shadow-red-600/40">删除</button>
          </div>
        </div>
      </div>
    </div>

    <!-- 文件预览弹窗 -->
    <div v-if="showPreviewModal" class="fixed inset-0 z-[70]">
      <div class="absolute inset-0 bg-black/70 backdrop-blur-sm" @click="closePreviewModal"></div>
      <div class="relative flex h-full w-full items-center justify-center p-4">
        <div class="relative flex h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-black/5">
          <!-- 头部 -->
          <div class="relative flex items-center justify-between bg-gradient-to-r from-primary-600 to-primary-500 px-6 py-3">
            <div class="absolute inset-0 bg-grid-white/10"></div>
            <div class="relative flex items-center gap-3 min-w-0">
              <div class="relative flex h-8 w-8 items-center justify-center rounded-lg bg-white/10 text-white">
                <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
              </div>
              <h3 class="text-sm font-medium text-white truncate">{{ previewFileName }}</h3>
            </div>
            <button @click="closePreviewModal" class="relative cursor-pointer rounded-full p-1.5 text-white/80 transition-colors hover:bg-white/20 hover:text-white">
              <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <!-- 预览区域 -->
          <div class="flex-1 overflow-hidden bg-slate-100">
            <iframe
              v-if="previewUrl"
              :src="previewUrl"
              class="h-full w-full border-0"
              frameborder="0"
            ></iframe>
          </div>
        </div>
      </div>
    </div>

    <!-- 更多操作下拉菜单 (Teleport到body避免overflow问题) -->
    <Teleport to="body">
      <div
        v-if="openMoreId !== null"
        class="fixed z-[9999] w-40 overflow-hidden rounded-xl border border-slate-200 bg-white py-1.5 shadow-xl ring-1 ring-black/5"
        :style="moreMenuStyle"
        @click.stop
      >
        <button
          v-if="openMoreItem?.status === 0"
          v-permission="'kb:contract:edit'"
          @click="handlePublish(openMoreItem)"
          class="flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left text-xs text-slate-700 transition-colors hover:bg-slate-50"
        >
          <svg class="h-4 w-4 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          发布
        </button>
        <button
          v-if="openMoreItem?.status === 1"
          v-permission="'kb:contract:edit'"
          @click="handleDeprecate(openMoreItem)"
          class="flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left text-xs text-slate-700 transition-colors hover:bg-slate-50"
        >
          <svg class="h-4 w-4 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" /></svg>
          废止
        </button>
        <button
          v-permission="'kb:contract:edit'"
          @click="openNewVersionModal(openMoreItem)"
          class="flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left text-xs text-slate-700 transition-colors hover:bg-slate-50"
        >
          <svg class="h-4 w-4 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
          上传新版本
        </button>
        <div class="my-1 border-t border-slate-100"></div>
        <button
          v-permission="'kb:contract:edit'"
          @click="confirmDeleteItem(openMoreItem)"
          class="flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left text-xs text-red-600 transition-colors hover:bg-red-50"
        >
          <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
          删除
        </button>
      </div>
    </Teleport>
    </div>
  </ListPageShell>
</template>

<script setup>
import { onMounted, onUnmounted, reactive, ref, computed } from 'vue'
import { ElMessage } from 'element-plus'
import KbCategoryTree from './components/KbCategoryTree.vue'
import AppPagination from '@/components/AppPagination.vue'
import ListPageShell from '@/components/ListPageShell.vue'
import PageFilterBar from '@/components/PageFilterBar.vue'
import { uploadFile } from '@/api/file'
import { useKbCategoryManagement } from './composables/useKbCategoryManagement'
import {
  getContractCategoryTree, createContractCategory, updateContractCategory, deleteContractCategory,
  getContractTemplatePage, getContractTemplateDetail, createContractTemplate, updateContractTemplate, deleteContractTemplate,
  publishContractTemplate, deprecateContractTemplate,
  getContractVersions, getContractVersionDetail, createContractVersion, rollbackContractTemplate,
  downloadContractAttachment, previewContractAttachment
} from '@/api/kb'

// ==================== 分类 ====================
const categoryState = useKbCategoryManagement({
  loadCategoryTree: getContractCategoryTree,
  createCategory: createContractCategory,
  updateCategory: updateContractCategory,
  defaultStatus: 1,
  messages: {
    loadError: '\u52a0\u8f7d\u5206\u7c7b\u5931\u8d25',
    emptyNameWarning: '\u8bf7\u8f93\u5165\u5206\u7c7b\u540d\u79f0',
    updateSuccess: '\u5206\u7c7b\u66f4\u65b0\u6210\u529f',
    createSuccess: '\u5206\u7c7b\u521b\u5efa\u6210\u529f',
    saveError: '\u4fdd\u5b58\u5206\u7c7b\u5931\u8d25'
  }
})
const categoryTree = categoryState.categoryTree
const categoryLoading = categoryState.categoryLoading
const selectedCategoryId = categoryState.selectedCategoryId
const showCategoryModal = categoryState.showCategoryModal
const isCategoryEdit = categoryState.isCategoryEdit
const categoryForm = categoryState.categoryForm
const loadCategories = categoryState.loadCategories
const flatCategories = categoryState.flatCategories
const getCategoryName = categoryState.getCategoryName
const handleCategorySelect = categoryState.handleCategorySelect
const handleAddCategory = categoryState.handleAddCategory
const handleAddChildCategory = categoryState.handleAddChildCategory
const handleEditCategory = categoryState.handleEditCategory
const closeCategoryModal = categoryState.closeCategoryModal
const submitCategory = categoryState.submitCategory

const dataList = ref([])
const listLoading = ref(false)
const searchKeyword = ref('')
const searchStatus = ref('')
const pageSize = ref(20)
const pageInfo = reactive({ current: 1, total: 0, hasPrevious: false, hasNext: false })

const loadList = async (page = 1) => {
  listLoading.value = true
  try {
    const data = await getContractTemplatePage({
      page, size: pageSize.value,
      categoryId: selectedCategoryId.value || undefined,
      keyword: searchKeyword.value || undefined,
      status: searchStatus.value === '' ? undefined : Number(searchStatus.value)
    })
    dataList.value = data.records || []
    Object.assign(pageInfo, { current: data.current || 1, total: data.total || 0, hasPrevious: data.hasPrevious, hasNext: data.hasNext })
  } catch (error) { ElMessage.error(error.message || '加载列表失败') }
  finally { listLoading.value = false }
}

const resetFilters = () => { searchKeyword.value = ''; searchStatus.value = ''; selectedCategoryId.value = null; loadList(1) }
const handlePageChange = (page) => { loadList(page) }

const handleCategoryTreeSelect = (id) => { handleCategorySelect(id, () => loadList(1)) }
const contractStatusText = (s) => ({ 0: '草稿', 1: '已发布', 2: '已废止' }[s] || '未知')
const contractStatusClass = (s) => ({
  0: 'status-badge bg-slate-100 text-slate-600',
  1: 'status-badge bg-emerald-100 text-emerald-700',
  2: 'status-badge bg-red-100 text-red-700'
}[s] || 'status-badge bg-slate-100 text-slate-600')

// ==================== 表单弹窗 ====================
const showFormModal = ref(false)
const isEdit = ref(false)
const formSubmitting = ref(false)
const form = reactive({
  id: null,
  categoryId: '',
  name: '',
  description: '',
  remark: '',
  files: [],                // 新增的文件
  existingAttachments: []   // 已有的附件（保留的）
})

const openFormModal = async (item) => {
  if (item) {
    isEdit.value = true
    try {
      const data = await getContractTemplateDetail(item.id)
      Object.assign(form, {
        id: data.id,
        categoryId: data.categoryId,
        name: data.name,
        description: data.description || '',
        remark: data.remark || '',
        files: [],
        existingAttachments: data.attachments || []
      })
    } catch (error) { ElMessage.error(error.message || '加载详情失败'); return }
  } else {
    isEdit.value = false
    Object.assign(form, {
      id: null,
      categoryId: selectedCategoryId.value || '',
      name: '',
      description: '',
      remark: '',
      files: [],
      existingAttachments: []
    })
  }
  showFormModal.value = true
}

const closeFormModal = () => { showFormModal.value = false }

// 选择附件
const handleAttachmentSelect = (e) => {
  const files = Array.from(e.target.files || [])
  const validTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document']
  for (const file of files) {
    if (!validTypes.includes(file.type)) {
      ElMessage.warning(`文件 "${file.name}" 格式不支持，仅支持 PDF、DOC、DOCX`)
      continue
    }
    form.files.push(file)
  }
  e.target.value = '' // 清空 input 以便重复选择同一文件
}

// 移除待上传的新文件
const removeNewFile = (idx) => {
  form.files.splice(idx, 1)
}

// 删除已有附件
const removeExistingAttachment = (att) => {
  form.existingAttachments = form.existingAttachments.filter(a => a.id !== att.id)
}

const submitForm = async () => {
  if (!form.name.trim()) { ElMessage.warning('请输入模板名称'); return }
  if (!form.categoryId) { ElMessage.warning('请选择分类'); return }

  // 验证附件：新增时必须有文件，编辑时至少保留一个附件
  const totalAttachments = form.existingAttachments.length + form.files.length
  if (!isEdit.value && !form.files.length) {
    ElMessage.warning('请上传模板文件')
    return
  }
  if (isEdit.value && totalAttachments === 0) {
    ElMessage.warning('至少保留一个模板文件')
    return
  }

  formSubmitting.value = true
  try {
    if (isEdit.value) {
      // 编辑模式：先上传新附件，再一次性提交所有更新
      const newAttachments = []
      for (let i = 0; i < form.files.length; i++) {
        const file = form.files[i]
        const uploadRes = await uploadFile(file, 'contract', 'public')
        const ext = file.name.split('.').pop()?.toLowerCase() || ''
        newAttachments.push({
          fileName: file.name,
          fileKey: uploadRes.fileName,
          fileSize: file.size,
          fileType: ext,
          sort: form.existingAttachments.length + i
        })
      }

      await updateContractTemplate(form.id, {
        id: form.id,
        categoryId: form.categoryId,
        name: form.name,
        description: form.description,
        remark: form.remark,
        keepAttachmentIds: form.existingAttachments.map(a => a.id),
        newAttachments
      })
      ElMessage.success('模板更新成功')
    } else {
      // 新增模式：先上传附件，再创建模板
      const attachments = []
      for (let i = 0; i < form.files.length; i++) {
        const file = form.files[i]
        const uploadRes = await uploadFile(file, 'contract', 'public')
        const ext = file.name.split('.').pop()?.toLowerCase() || ''
        attachments.push({
          fileName: file.name,
          fileKey: uploadRes.fileName,
          fileSize: file.size,
          fileType: ext,
          sort: i
        })
      }
      await createContractTemplate({
        categoryId: form.categoryId,
        name: form.name,
        description: form.description,
        remark: form.remark,
        attachments
      })
      ElMessage.success('模板创建成功')
    }
    closeFormModal()
    await loadList(pageInfo.current)
  } catch (error) {
    ElMessage.error(error.message || '保存失败')
  } finally {
    formSubmitting.value = false
  }
}

// ==================== 详情 ====================
const showDetailModal = ref(false)
const detailData = ref(null)
const detailLoading = ref(false)
const previewLoading = reactive({})

const openDetailModal = async (item) => {
  showDetailModal.value = true
  detailLoading.value = true
  try { detailData.value = await getContractTemplateDetail(item.id) }
  catch (error) { ElMessage.error(error.message || '加载详情失败') }
  finally { detailLoading.value = false }
}

const formatFileSize = (bytes) => {
  if (!bytes) return '-'
  if (bytes < 1024) return bytes + ' B'
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
}

const handleDownload = async (att) => {
  try {
    const blob = await downloadContractAttachment(att.id)
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = att.fileName
    a.click()
    window.URL.revokeObjectURL(url)
  } catch (error) { ElMessage.error(error.message || '下载失败') }
}

// 预览附件
const showPreviewModal = ref(false)
const previewUrl = ref('')
const previewFileName = ref('')

const handlePreview = async (att) => {
  // 仅支持 PDF 预览，其他格式提示下载
  const ext = att.fileType?.toLowerCase()
  if (ext !== 'pdf') {
    ElMessage.warning('仅支持 PDF 文件预览，其他格式请下载后查看')
    return
  }

  previewLoading[att.id] = true
  try {
    const response = await previewContractAttachment(att.id)
    // 释放之前的 URL
    if (previewUrl.value) {
      window.URL.revokeObjectURL(previewUrl.value)
    }
    const blob = new Blob([response], { type: 'application/pdf' })
    previewUrl.value = window.URL.createObjectURL(blob)
    previewFileName.value = att.fileName
    showPreviewModal.value = true
  } catch (error) {
    ElMessage.error(error.message || '预览加载失败')
  } finally {
    previewLoading[att.id] = false
  }
}

const closePreviewModal = () => {
  showPreviewModal.value = false
  if (previewUrl.value) {
    window.URL.revokeObjectURL(previewUrl.value)
    previewUrl.value = ''
  }
  previewFileName.value = ''
}

// ==================== 更多菜单 ====================
const openMoreId = ref(null)
const openMoreItem = ref(null)
const moreMenuPos = reactive({ top: 0, left: 0 })
const moreBtnRefs = {}

const moreMenuStyle = computed(() => ({
  top: `${moreMenuPos.top}px`,
  left: `${moreMenuPos.left}px`
}))

const setMoreBtnRef = (id, el) => {
  if (el) moreBtnRefs[id] = el
}

const toggleMoreMenu = (id, event) => {
  if (openMoreId.value === id) {
    openMoreId.value = null
    openMoreItem.value = null
  } else {
    const item = dataList.value.find(f => f.id === id)
    openMoreItem.value = item
    openMoreId.value = id
    // 计算菜单位置
    const btn = event.currentTarget
    const rect = btn.getBoundingClientRect()
    moreMenuPos.top = rect.bottom + 4
    moreMenuPos.left = rect.right - 144 // 144 = w-36 (9rem)
  }
}

const handleClickOutside = (e) => {
  if (openMoreId.value && !e.target.closest('.more-menu-container')) {
    openMoreId.value = null
    openMoreItem.value = null
  }
}

const handlePublish = async (item) => {
  openMoreId.value = null
  try { await publishContractTemplate(item.id); ElMessage.success('发布成功'); await loadList(pageInfo.current) }
  catch (error) { ElMessage.error(error.message || '发布失败') }
}

const handleDeprecate = async (item) => {
  openMoreId.value = null
  try { await deprecateContractTemplate(item.id); ElMessage.success('已废止'); await loadList(pageInfo.current) }
  catch (error) { ElMessage.error(error.message || '废止失败') }
}

// ==================== 新版本 ====================
const showNewVersionModal = ref(false)
const newVersionTarget = ref(null)
const newVersionForm = reactive({ changeSummary: '' })

const openNewVersionModal = (item) => {
  openMoreId.value = null
  newVersionTarget.value = item
  newVersionForm.changeSummary = ''
  showNewVersionModal.value = true
}

const submitNewVersion = async () => {
  try {
    await createContractVersion(newVersionTarget.value.id, { changeSummary: newVersionForm.changeSummary })
    ElMessage.success('新版本创建成功')
    showNewVersionModal.value = false
    await loadList(pageInfo.current)
  } catch (error) { ElMessage.error(error.message || '创建失败') }
}

// ==================== 版本历史 ====================
const showVersionDrawer = ref(false)
const versionList = ref([])
const versionLoading = ref(false)
const versionTarget = ref(null)

const openVersionDrawer = async (item) => {
  versionTarget.value = item
  showVersionDrawer.value = true
  versionLoading.value = true
  try { versionList.value = await getContractVersions(item.id) }
  catch (error) { ElMessage.error(error.message || '加载版本失败') }
  finally { versionLoading.value = false }
}

const handleRollback = async (version) => {
  try {
    await rollbackContractTemplate(versionTarget.value.id, version.versionNo)
    ElMessage.success('回滚成功')
    showVersionDrawer.value = false
    await loadList(pageInfo.current)
  } catch (error) { ElMessage.error(error.message || '回滚失败') }
}

// 查看指定版本详情
const viewVersionDetail = async (version) => {
  showDetailModal.value = true
  detailLoading.value = true
  try {
    detailData.value = await getContractVersionDetail(versionTarget.value.id, version.versionNo)
  } catch (error) {
    ElMessage.error(error.message || '加载版本详情失败')
  } finally {
    detailLoading.value = false
  }
}

// ==================== 删除 ====================
const showConfirmModal = ref(false)
const confirmMessage = ref('')
const confirmPayload = ref({ type: '', id: null })

const confirmDeleteCategory = (node) => {
  confirmPayload.value = { type: 'category', id: node.id }
  confirmMessage.value = `确认删除分类「${node.name}」？`
  showConfirmModal.value = true
}

const confirmDeleteItem = (item) => {
  openMoreId.value = null
  confirmPayload.value = { type: 'template', id: item.id }
  confirmMessage.value = `确认删除合同范本「${item.name}」？`
  showConfirmModal.value = true
}

const confirmDeleteAction = async () => {
  const { type, id } = confirmPayload.value
  try {
    if (type === 'category') { await deleteContractCategory(id); ElMessage.success('分类已删除'); await loadCategories() }
    else { await deleteContractTemplate(id); ElMessage.success('范本已删除'); await loadList(pageInfo.current) }
  } catch (error) { ElMessage.error(error.message || '删除失败') }
  finally { showConfirmModal.value = false }
}

// ==================== 初始化 ====================
onMounted(async () => {
  document.addEventListener('click', handleClickOutside)
  await loadCategories()
  await loadList(1)
})
onUnmounted(() => { document.removeEventListener('click', handleClickOutside) })
</script>

<style scoped>
/* 分类弹窗过渡动画 */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.25s ease;
}
.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}
.modal-enter-active > div:last-child > div,
.modal-leave-active > div:last-child > div {
  transition: transform 0.3s ease, opacity 0.25s ease;
}
.modal-enter-from > div:last-child > div {
  transform: translateY(16px);
  opacity: 0;
}
.modal-leave-to > div:last-child > div {
  transform: translateY(8px);
  opacity: 0;
}
</style>
